import type {TFunction} from 'i18next';

export type WizardStepKey = 'contracts' | 'evidence' | 'calculation' | 'approval' | 'finalization';

export interface WizardStep {
    key: WizardStepKey;
    label: string;
    done: boolean;
}

export interface PayrollWorkflowInputs {
    hasContracts: boolean;
    evidenceTotal: number;
    evidencePending: number;
    /** A payroll snapshot/calculation exists for the selected period. */
    financialsAvailable: boolean;
    isPeriodLocked: boolean;
}

export interface PayrollWorkflowState {
    steps: WizardStep[];
    activeStepIndex: number;
    isComplete: boolean;
    statusLabel: string;
}

/**
 * Single definition of the payroll workflow steps shown by PayrollWizardCard (dashboard, payroll,
 * work evidence approvals, reports). Approval/Finalization collapse onto the OPEN/LOCKED period
 * status the backend exposes today.
 */
export function buildPayrollWorkflow(t: TFunction, input: PayrollWorkflowInputs): PayrollWorkflowState {
    const contractsDone = input.hasContracts;
    const evidenceDone = contractsDone && input.evidenceTotal > 0 && input.evidencePending === 0;
    const calculationDone = evidenceDone && input.financialsAvailable;
    const approvalDone = calculationDone && input.isPeriodLocked;
    const finalizationDone = approvalDone;

    const steps: WizardStep[] = [
        {key: 'contracts', label: t('dashboard.wizard.steps.contracts'), done: contractsDone},
        {key: 'evidence', label: t('dashboard.wizard.steps.evidence'), done: evidenceDone},
        {key: 'calculation', label: t('dashboard.wizard.steps.calculation'), done: calculationDone},
        {key: 'approval', label: t('dashboard.wizard.steps.approval'), done: approvalDone},
        {key: 'finalization', label: t('dashboard.wizard.steps.finalization'), done: finalizationDone},
    ];
    const currentIndex = steps.findIndex((s) => !s.done);
    const isComplete = currentIndex === -1;
    return {
        steps,
        activeStepIndex: isComplete ? steps.length - 1 : currentIndex,
        isComplete,
        statusLabel: isComplete ? t('dashboard.wizard.status.completed') : t('dashboard.wizard.status.inProgress'),
    };
}
